import { useMemo, useRef, useState, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { CButton, CButtonGroup } from "@coreui/react-pro";
import type { PortfolioGrowth } from "../../../api/hooks/usePortfolioBuilder";
import {
  addMonths,
  formatDrawdown,
  formatMonth,
  formatMoney,
  formatSignedWhole,
  formatThousands,
  monthsBetween,
  niceTicks,
  parseThousands,
} from "../portfolio-builder";
import { useEased } from "../useEased";
import { useElementSize } from "../useElementSize";

const PAD = { left: 46, right: 8, top: 8, bottom: 16 };

interface WhatIfGrowthProps {
  firstMonth: string;
  lastMonth: string;
  /** The month-end the money goes in at. */
  start: string;
  onStartChange: (month: string) => void;
  amountThousands: number;
  onAmountChange: (thousands: number) => void;
  /** Null until the first answer arrives. */
  answer: PortfolioGrowth | null;
  /** The line couldn't be worked out, for a reason other than the month. */
  failed: boolean;
}

/**
 * What an amount the customer types would have grown to from a month they choose: by the quick
 * buttons, or by tapping or dragging on the chart. The line is month-end values from that month on.
 */
export function WhatIfGrowth({
  firstMonth,
  lastMonth,
  start,
  onStartChange,
  amountThousands,
  onAmountChange,
  answer,
  failed,
}: WhatIfGrowthProps) {
  const { t } = useTranslation("portfolioBuilder");
  const hostRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(hostRef);
  const [typed, setTyped] = useState<string | null>(null);
  const [dragMonth, setDragMonth] = useState<string | null>(null);

  const monthCount = monthsBetween(firstMonth, lastMonth) + 1;
  const shownStart = dragMonth ?? start;
  const amountUsd = amountThousands * 1000;
  const growth = answer?.kind === "growth" ? answer.growth : null;
  const tooLate = answer?.kind === "startTooLate" ? answer : null;

  // Eased in log terms, as a growth line is read, and relative to what went in, so a new amount
  // rescales the line without a request or an animation.
  const target = useMemo(
    () => growth?.months.map((month) => Math.log10(month.value / growth.amount)) ?? null,
    [growth],
  );
  const continuity = growth ? `${growth.start}|${growth.months.length}` : null;
  const eased = useEased(target, continuity);

  const quickStarts = [
    { label: t("whatIf.quick.oneYear"), back: 12 },
    { label: t("whatIf.quick.years", { count: 2 }), back: 24 },
    { label: t("whatIf.quick.years", { count: 3 }), back: 36 },
    { label: t("whatIf.quick.years", { count: 5 }), back: 60 },
    { label: t("whatIf.quick.since", { year: firstMonth.slice(0, 4) }), back: monthCount - 1 },
  ]
    .filter((quick) => quick.back <= monthCount - 1)
    .map((quick) => ({ label: quick.label, month: addMonths(lastMonth, -quick.back) }));

  const width = Math.max(240, size.width);
  const height = Math.max(80, size.height);
  const plotWidth = width - PAD.left - PAD.right;
  const x = (monthIndex: number) => PAD.left + (monthIndex / (monthCount - 1)) * plotWidth;

  const monthAt = (event: PointerEvent<HTMLDivElement>) => {
    const left = event.currentTarget.getBoundingClientRect().left;
    const index = Math.round(((event.clientX - left - PAD.left) / plotWidth) * (monthCount - 1));
    return addMonths(firstMonth, Math.min(monthCount - 2, Math.max(0, index)));
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragMonth(monthAt(event));
  };
  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (dragMonth === null) return;
    setDragMonth(monthAt(event));
  };
  // The line is asked for once the finger lifts, not for every month it passes over.
  const handlePointerUp = () => {
    if (dragMonth === null) return;
    onStartChange(dragMonth);
    setDragMonth(null);
  };

  const markX = x(monthsBetween(firstMonth, shownStart));
  const before =
    markX > PAD.left ? (
      <>
        <rect
          className="portfolio-growth-before"
          x={PAD.left}
          y={PAD.top}
          width={markX - PAD.left}
          height={height - PAD.top - PAD.bottom}
          rx={4}
        />
        {markX - PAD.left > 70 && (
          <text
            className="portfolio-growth-hint"
            x={(PAD.left + markX) / 2}
            y={(PAD.top + height - PAD.bottom) / 2 + 3}
            textAnchor="middle"
          >
            {t("whatIf.tapEarlier")}
          </text>
        )}
      </>
    ) : null;
  const mark = (
    <line className="portfolio-growth-mark" x1={markX} x2={markX} y1={PAD.top} y2={height - PAD.bottom} />
  );

  const chart = (() => {
    if (size.width === 0) return null;

    // Nothing to draw a line for, but the month asked for still shows where it is.
    if (!growth || !eased) {
      return (
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t("whatIf.chartLabel")}>
          {before}
          {mark}
        </svg>
      );
    }

    const values = eased.map((relative) => amountUsd * 10 ** relative);
    const low = Math.min(...values);
    const high = Math.max(...values);
    const logScale = high / low > 15;
    const scaled = (value: number) => (logScale ? Math.log10(value) : value);
    const span =
      scaled(high) - scaled(low) > 1e-9 ? scaled(high) - scaled(low) : Math.abs(scaled(high)) * 0.1 || 1;
    const yLow = scaled(low) - span * 0.06;
    const yHigh = scaled(high) + span * 0.08;
    const y = (value: number) =>
      height - PAD.bottom - ((scaled(value) - yLow) / (yHigh - yLow)) * (height - PAD.top - PAD.bottom);

    const firstIndex = monthsBetween(firstMonth, growth.start);
    const points = values.map((value, index) => `${x(firstIndex + index).toFixed(1)},${y(value).toFixed(1)}`);

    const ticks = logScale
      ? (() => {
          const decades: number[] = [];
          for (let power = Math.ceil(Math.log10(low)); power <= Math.log10(high); power++) {
            decades.push(10 ** power);
          }
          return decades.length ? decades : [low, high];
        })()
      : niceTicks(low, high, 2);

    const years = Array.from({ length: monthCount }, (_, index) => addMonths(firstMonth, index))
      .map((month, index) => ({ month, index }))
      .filter(({ month }) => month.endsWith("-01"));

    return (
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t("whatIf.chartLabel")}>
        {before}
        {ticks.map((tick) => {
          const tickY = y(tick);
          if (tickY < PAD.top - 1 || tickY > height - PAD.bottom + 1) return null;
          return (
            <g key={tick}>
              <line className="portfolio-chart-grid" x1={markX} x2={width - PAD.right} y1={tickY} y2={tickY} />
              <text className="portfolio-chart-tick" x={PAD.left - 5} y={tickY + 4} textAnchor="end">
                {formatMoney(tick)}
              </text>
            </g>
          );
        })}
        {years.map(({ month, index }) => (
          <text key={month} className="portfolio-chart-tick" x={x(index)} y={height - 3} textAnchor="middle">
            ’{month.slice(2, 4)}
          </text>
        ))}
        <path
          className="portfolio-growth-area"
          d={`M${x(firstIndex)},${height - PAD.bottom}L${points.join("L")}L${x(monthCount - 1)},${height - PAD.bottom}Z`}
        />
        <path className="portfolio-growth-line" d={`M${points.join("L")}`} />
        {mark}
        <circle
          className="portfolio-growth-end"
          cx={x(monthCount - 1)}
          cy={y(values[values.length - 1])}
          r={3.5}
        />
      </svg>
    );
  })();

  const worth = growth ? (growth.worth * amountUsd) / growth.amount : null;

  return (
    <section className="portfolio-what-if">
      <div className="portfolio-what-if-ask">
        {t("whatIf.putIn")}{" "}
        <label className="portfolio-amount">
          {t("whatIf.amountPrefix")}
          <input
            inputMode="decimal"
            autoComplete="off"
            aria-label={t("whatIf.amountLabel")}
            value={typed ?? formatThousands(amountThousands)}
            onChange={(event) => {
              setTyped(event.target.value);
              onAmountChange(parseThousands(event.target.value));
            }}
            onBlur={() => setTyped(null)}
          />
          {t("whatIf.amountSuffix")}
        </label>{" "}
        {t("whatIf.atTheEndOf")} <strong className="text-primary">{formatMonth(shownStart)}</strong>
      </div>

      <div className="portfolio-what-if-result">
        <span className="portfolio-what-if-label">{t("whatIf.worth")}</span>
        {growth && worth !== null && (
          <>
            <strong>{formatMoney(worth, worth < 1e7)}</strong>
            <span className={growth.gainPct >= 0 ? "text-success" : "text-danger"}>
              {t("whatIf.gain", { gain: formatSignedWhole(growth.gainPct), month: formatMonth(lastMonth) })}
            </span>
          </>
        )}
        {tooLate && (
          <span className="portfolio-what-if-message">
            {tooLate.latestStart
              ? t("whatIf.startTooLate", { month: formatMonth(tooLate.latestStart) })
              : t("whatIf.startTooLateNoMonth")}
          </span>
        )}
        {failed && <span className="portfolio-what-if-message">{t("whatIf.failed")}</span>}
      </div>

      <CButtonGroup size="sm" className="btn-group-pill" role="group" aria-label={t("whatIf.startLabel")}>
        {quickStarts.map((quick) => (
          <CButton
            key={quick.month}
            color="primary"
            variant={start === quick.month ? undefined : "outline"}
            aria-pressed={start === quick.month}
            onClick={() => onStartChange(quick.month)}
          >
            {quick.label}
          </CButton>
        ))}
      </CButtonGroup>

      <div
        ref={hostRef}
        className="portfolio-growth"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {chart}
      </div>

      <p className="portfolio-note mb-0">
        {growth ? t("whatIf.biggestFall", { fall: formatDrawdown(growth.worstDrawdownPct) }) : " "}
      </p>
    </section>
  );
}
